import React, { useState } from "react";
import { Modal } from "@mui/material";
import { Visibility as PreviewIcon } from "@mui/icons-material";

import {
    ButtonRow,
    Expandable,
    InfoText,
    ModalWrapper,
    StyledBackdrop,
    TitleText,
    TitleWrapper,
} from "../../MaterialOrder/single/AddMaterialModal/Styled";
import { StyledButton } from "../../MaterialOrder/Styled";
import { ImportMaterialForm } from "./ImportMaterialForm";
import { ImportMaterialList } from "./ImportMaterialList";
import { api } from "../../../shared/hooks/api";
import { success, error } from "../../../utils/toast";
import useMergeState from "../../../shared/hooks/useMergeState";
import LoadingButton from "../../../shared/components/LoadingButton";
import Template from "../../../templates/PMS - Phiếu nhập kho vật tư.docx";
import { generateDocumentBlob } from "../../../utils/exportUtils";
import { buildDocxData } from "../../../utils/docxBuilder";
import { materialImportPreviewDescriptor } from "../../../utils/docxMappings";
import DocxPreviewModal from "../../../shared/components/DocxPreviewModal";

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

interface ImportMaterialModalProps {
    open: boolean;
    handleClose: () => void;
}

const getInitialState = () => {
    return {
        formData: {
            provider_name: "",
            deliverer: "",
            input_method: "",
            import_date: new Date().toLocaleDateString('en-CA'),
            note: "",
        },
        selectedMaterials: []
    };
};

export const ImportMaterialModal = ({ open, handleClose }: ImportMaterialModalProps) => {
    const [state, mergeState] = useMergeState(getInitialState());
    const [{ isCreating }, createImport] = api.post("/materials/imports/");
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

    const mergeFormData = (newFormData: any) => {
        mergeState({ formData: { ...state.formData, ...newFormData } });
    };

    const mergeSelectedMaterials = (newSelectedMaterials: any) => {
        mergeState({ selectedMaterials: newSelectedMaterials });
    };

    const handleCloseWithAnimation = () => {
        setTimeout(handleClose, 200);
    };

    const validateImportData = (): ValidationResult => {
        const errors: string[] = [];

        if (!state.formData.provider_name.toString().trim()) {
            errors.push("Tên nhà cung cấp không được để trống.");
        }

        if (!state.formData.input_method.toString().trim()) {
            errors.push("Phương thức nhập không được để trống.");
        }

        if (!state.formData.import_date || isNaN(new Date(state.formData.import_date).getTime())) {
            errors.push("Ngày nhập không hợp lệ.");
        }

        if (!state.selectedMaterials.length) {
            errors.push("Danh sách vật tư không được để trống.");
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    };

    const handlePreview = async () => {
        const validation = validateImportData();
        if (!validation.isValid) {
            alert("Vui lòng điền đầy đủ thông tin trước khi xem trước:\n\n" + validation.errors.join("\n\n"));
            return;
        }

        try {
            const data = buildDocxData(state, materialImportPreviewDescriptor);
            const blob = await generateDocumentBlob(data, Template);
            setPreviewBlob(blob);
            setIsPreviewOpen(true);
        } catch (e) {
            console.error("Failed to generate import preview DOCX:", e);
            alert("Không thể tạo bản xem trước tài liệu.");
        }
    };

    const handleImport = async () => {
        const validation = validateImportData();
        if (!validation.isValid) {
            alert(validation.errors.join("\n\n"));
            return;
        }

        try {
            const importData = {
                ...state.formData,
                materials: state.selectedMaterials.map((item: any) => ({
                    material: item.id,
                    quantity: item.importQuantity,
                    due_date: item.import_date,
                    note: item.note || "",
                })),
            };

            await createImport(importData);
            success("THÀNH CÔNG", "Nhập kho vật tư thành công");

            handleCloseWithAnimation();
        } catch (err) {
            error("Không thể nhập kho vật tư", err);
        }
    };

    return (
        <>
        <Modal
            open={open}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <ModalWrapper isOpen={open}>
                <Expandable>
                    <TitleWrapper>
                        <TitleText>Nhập kho vật tư</TitleText>
                        <InfoText>Vui lòng điền thông tin nhập kho và chọn vật tư</InfoText>
                    </TitleWrapper>

                    <ImportMaterialForm formData={state.formData} onChange={mergeFormData} />

                    <TitleText>Danh sách nhập kho vật tư</TitleText>

                    <ImportMaterialList
                        selectedMaterials={state.selectedMaterials}
                        setSelectedMaterials={mergeSelectedMaterials}
                    />
                </Expandable>

                <ButtonRow>
                    <StyledButton variant="outlined" onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                    <StyledButton onClick={handlePreview} startIcon={<PreviewIcon />} variant="contained">
                        Xem trước phiếu
                    </StyledButton>
                </ButtonRow>
            </ModalWrapper>
        </Modal>
        <DocxPreviewModal
            open={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            blob={previewBlob}
            title="Xem trước phiếu nhập kho"
            BackdropComponent={StyledBackdrop}
            backdropProps={{ timeout: 300 }}
            debug
            actions={
                <LoadingButton
                    variant="contained"
                    onClick={handleImport}
                    disabled={isCreating}
                    isLoading={isCreating}
                    text="Nhập kho"
                />
            }
        />
        </>
    );
};
